import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Users, Shield, Lightning, Target, Rocket, Star } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const WhatToExpect = () => {
  const expectations = [
    {
      icon: <Clock className="w-8 h-8" weight="thin" />,
      title: "Snelle Oplevering",
      description: "Je website kan binnen 2-4 weken live staan. Geen maanden wachten, maar direct resultaat."
    },
    {
      icon: <Users className="w-8 h-8" weight="thin" />,
      title: "Persoonlijke Aanpak",
      description: "We werken samen met jou, niet voor jou. Jouw visie staat centraal in elk besluit."
    },
    {
      icon: <Shield className="w-8 h-8" weight="thin" />,
      title: "Betrouwbare Technologie",
      description: "Gebouwd met moderne, veilige technologie die schaalt met je groei."
    },
    {
      icon: <Lightning className="w-8 h-8" weight="thin" />,
      title: "Direct Resultaat",
      description: "Van dag 1 kun je verkopen. Geen complexe setup, maar direct aan de slag."
    },
    {
      icon: <Target className="w-8 h-8" weight="thin" />,
      title: "Data-Gedreven",
      description: "Inzichten in klantgedrag om je website continu te verbeteren."
    },
    {
      icon: <Rocket className="w-8 h-8" weight="thin" />,
      title: "Groeit Met Je Mee",
      description: "Je website groeit mee met je bedrijf. Upgrade wanneer je dat wilt."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-500 mb-6">
            Wat je kan <span className="text-slate-600">verwachten</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Van het eerste gesprek tot je website live staat. Hier is wat je van ons kunt verwachten 
            tijdens het hele proces.
          </p>
        </motion.div>

        {/* Expectations Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
        >
          {expectations.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              <div className="bg-white rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-200">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 text-white rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-blue-500 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-slate-500/5 to-slate-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-blue-500 rounded-3xl p-12 text-white">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-6">
              <Star className="w-8 h-8 text-blue-500" weight="fill" />
            </div>
            <h3 className="text-3xl font-bold mb-4">Klaar om te starten?</h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
              We maken je website precies zoals jij het wilt. Geen verrassingen, 
              alleen een professioneel resultaat.
            </p>
            <Link to="/start-your-project" className="inline-flex items-center bg-white text-blue-500 px-10 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300">
              <span>Start je project</span>
              <Rocket className="w-5 h-5 ml-2" weight="thin" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatToExpect;
